import React from 'react';
import { Button, Field, FieldLabel } from '@/components/ui';
import { ImageIcon, X } from 'lucide-react';

const MAX_BYTES = 5 * 1024 * 1024;

export type LessonCoverImageFieldProps = {
  /** Saved URL and/or temporary object URL for preview */
  displayUrl: string;
  uploading: boolean;
  disabled?: boolean;
  inputId: string;
  onFileSelect: (file: File) => void;
  onRemove: () => void;
  description?: string;
};

/**
 * Optional cover image for public lesson cards (Firebase Storage URL stored on lesson).
 */
export function LessonCoverImageField({
  displayUrl,
  uploading,
  disabled,
  inputId,
  onFileSelect,
  onRemove,
  description = 'Shown on your public academy and lessons pages. Max 5MB, images only.',
}: LessonCoverImageFieldProps) {
  return (
    <Field>
      <FieldLabel htmlFor={inputId}>Cover image (optional)</FieldLabel>
      <p className="text-sm text-muted-foreground mb-2">{description}</p>
      {displayUrl ? (
        <div className="relative rounded-md border overflow-hidden max-w-md aspect-video bg-muted">
          <img src={displayUrl} alt="" className="w-full h-full object-cover" />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="absolute top-2 right-2"
            disabled={disabled || uploading}
            onClick={onRemove}
          >
            <X className="h-4 w-4 mr-1" />
            Remove
          </Button>
        </div>
      ) : null}
      <div className="flex items-center gap-2 mt-2">
        <input
          id={inputId}
          type="file"
          accept="image/*"
          className="sr-only"
          disabled={disabled || uploading}
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (!f) return;
            if (!f.type.startsWith('image/')) {
              return;
            }
            if (f.size > MAX_BYTES) {
              return;
            }
            onFileSelect(f);
          }}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || uploading}
          onClick={() => document.getElementById(inputId)?.click()}
        >
          <ImageIcon className="h-4 w-4 mr-2" />
          {uploading ? 'Uploading…' : displayUrl ? 'Replace image' : 'Upload image'}
        </Button>
      </div>
    </Field>
  );
}
